import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, TextField, TextFieldProps, Typography } from '@mui/material';
import { DateCalendar, DateCalendarProps } from '@mui/x-date-pickers/DateCalendar';
import { PickerDay, PickerDayProps } from '@mui/x-date-pickers/PickerDay';
import { format as formatDate, isSameDay } from 'date-fns';
import clsx from 'clsx';
import { APP_FORMAT_DATE } from 'configs/const';
import { useTranslate } from 'hooks/use-translate';
import { convertToDate, DateValue } from 'utils/dates';

/**
 * Date-range picker (Phase D1: rewritten on @mui/x-date-pickers; the v3
 * version cloned pickers' internal Day / PickerToolbar and styled them with
 * JSS). @mui/x-date-pickers has no free range picker, so the begin / end
 * state machine stays here: first click = begin, second = end (sorted; the
 * end gets 23:59:59.999 as before), third starts over. Styling lives in
 * `AurionCR/styles/form/_date-range-picker.scss`.
 *
 *  - `variant="static"` renders the calendar inline (DateMultiple's tabs).
 *  - any other variant renders a read-only text field that opens a dialog;
 *    with `autoOk` (default) the dialog commits and closes on the end click.
 */
type FieldProps = Pick<
  TextFieldProps,
  'label' | 'placeholder' | 'helperText' | 'style' | 'className' | 'disabled' | 'fullWidth' | 'name' | 'onBlur' | 'size'
>;

interface Props extends FieldProps {
  value: DateValue[];
  onChange: (value: Date[]) => void;
  variant?: 'dialog' | 'inline' | 'static';
  format?: string;
  emptyLabel?: string;
  autoOk?: boolean;
  onClose?: () => void;
  error?: any;
  classes?: Record<string, any>;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
}

interface RangeDayProps extends PickerDayProps {
  begin?: Date;
  end?: Date;
}

const RangeDay = ({ begin, end, day, className, ...props }: RangeDayProps) => {
  const { min, max } = begin && end && begin > end ? { min: end, max: begin } : { min: begin, max: end };
  const isBegin = Boolean(min && isSameDay(day, min));
  const isEnd = Boolean(max && isSameDay(day, max));
  const isSelected = Boolean(min && max ? day >= min && day <= max : isBegin);
  return (
    <PickerDay
      {...props}
      day={day}
      selected={false}
      className={clsx(className, 'date-range-picker__day', {
        'date-range-picker__day--selected': isSelected,
        'date-range-picker__day--begin': isBegin,
        'date-range-picker__day--end': isEnd,
        'date-range-picker__day--today': props.today,
        'date-range-picker__day--outside': props.outsideCurrentMonth,
      })}
    />
  );
};

type CalendarProps = Pick<DateCalendarProps, 'minDate' | 'maxDate' | 'disablePast' | 'disableFuture' | 'disabled'>;

interface RangeCalendarProps extends CalendarProps {
  begin?: Date;
  end?: Date;
  referenceDate?: Date;
  format: string;
  emptyLabel?: string;
  onPickDay: (day: Date) => void;
}

const RangeCalendar = ({ begin, end, referenceDate, format, emptyLabel, onPickDay, ...calendar }: RangeCalendarProps) => {
  const title = useMemo(() => {
    const dates = [begin, end].filter(Boolean).sort((a, b) => Number(a) - Number(b)) as Date[];
    if (dates.length === 0) {
      return emptyLabel || '';
    }
    return `${formatDate(dates[0], format)} - ${dates[1] ? formatDate(dates[1], format) : '--/--/----'}`;
  }, [begin, end, format, emptyLabel]);
  return (
    <div className="date-range-picker">
      <div className="date-range-picker__toolbar">
        <Typography variant="h5" className="date-range-picker__title">
          {title}
        </Typography>
      </div>
      <DateCalendar
        {...calendar}
        value={null}
        referenceDate={referenceDate}
        views={['year', 'month', 'day']}
        openTo="day"
        onChange={(day, selectionState, selectedView) => {
          if (day && selectedView === 'day') {
            onPickDay(day);
          }
        }}
        slots={{ day: RangeDay }}
        slotProps={{ day: { begin, end } as any }}
      />
    </div>
  );
};

const toDate = (value: DateValue | undefined): Date | undefined => (value ? convertToDate(value) : undefined);

const DateRangePickerComponent: React.FC<Props> = ({
  classes,
  value,
  onChange,
  format = APP_FORMAT_DATE,
  emptyLabel,
  autoOk = true,
  onClose,
  variant = 'dialog',
  label,
  placeholder,
  helperText,
  error,
  style,
  className,
  disabled,
  fullWidth,
  name,
  onBlur,
  size,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
}) => {
  const { t } = useTranslate();
  const [begin, setBegin] = useState<Date | undefined>(toDate(value[0]));
  const [end, setEnd] = useState<Date | undefined>(toDate(value[1]));
  const [open, setOpen] = useState(false);

  const callOnChange = useCallback(
    (dates: (Date | undefined)[]) => {
      const dates_ = (dates.filter(Boolean) as Date[]).sort((a, b) => Number(a) - Number(b));
      if (dates_[1]) {
        dates_[1].setHours(23, 59, 59, 999);
      }
      onChange(dates_);
    },
    [onChange],
  );

  const onPickDay = useCallback(
    (day: Date) => {
      if (!begin) {
        setBegin(day);
      } else if (!end) {
        setEnd(day);
        if (autoOk) {
          callOnChange([begin, day]);
          setOpen(false);
        }
      } else {
        setBegin(day);
        setEnd(undefined);
      }
    },
    [begin, end, autoOk, callOnChange],
  );

  useEffect(() => {
    setBegin(toDate(value[0]));
    setEnd(toDate(value[1]));
  }, [value]);

  const calendar = (
    <RangeCalendar
      begin={begin}
      end={end}
      referenceDate={toDate(value[0])}
      format={format}
      emptyLabel={emptyLabel}
      onPickDay={onPickDay}
      minDate={minDate}
      maxDate={maxDate}
      disablePast={disablePast}
      disableFuture={disableFuture}
      disabled={disabled}
    />
  );

  if (variant === 'static') {
    return calendar;
  }

  const labelText = begin && end ? `${formatDate(begin, format)} - ${formatDate(end, format)}` : emptyLabel || '';
  const handleClose = () => {
    callOnChange([begin, end]);
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };
  return (
    <>
      <TextField
        classes={classes}
        label={label}
        placeholder={placeholder}
        value={labelText}
        error={Boolean(error)}
        helperText={helperText}
        style={style}
        className={className}
        disabled={disabled}
        fullWidth={fullWidth}
        name={name}
        onBlur={onBlur}
        size={size}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        slotProps={{
          input: { readOnly: true }
        }}
      />
      <Dialog open={open} onClose={handleClose} className="date-range-picker__dialog">
        {calendar}
        <DialogActions>
          <Button
            color="primary"
            onClick={() => {
              setBegin(toDate(value[0]));
              setEnd(toDate(value[1]));
              setOpen(false);
              if (onClose) {
                onClose();
              }
            }}
          >
            {t('cancel')}
          </Button>
          <Button color="primary" onClick={handleClose}>
            {t('ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const DateRangePicker = memo(DateRangePickerComponent);
